import { Terminal } from '@stripe/terminal-js'
import { fetchApi } from '@vend/utilities'
import Stripe from 'stripe'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { isErrorResponse } from './errorChecking'
import { SwiperError, SwiperErrorResponse } from './lspay.types'
import { trackTerminalCompleteWithError } from './terminalTracking'
import { TRANSACTION_TYPE } from './lspayConstants'

export const createSetupIntent = async (
  reatilerPaymentTypeId: string,
  outletId: string
): Promise<string> => {
  const response = await fetchApi(
    `/api/2.0/payment_types/${reatilerPaymentTypeId}/lspay/setup-intent/${outletId}`,
    {
      method: 'POST',
    }
  )

  if (!response.ok) {
    const swiperError: SwiperErrorResponse = await response.json()

    throw new SwiperError(
      swiperError.error,
      swiperError.code,
      swiperError.fields
    )
  }

  return (await response.json()).client_secret as string
}

export const collectSetupIntent = async (
  terminal: Terminal,
  terminalMonitoringInstance: TerminalEventMonitoring,
  clientSecret: string,
  isRetry: boolean
): Promise<string> => {
  if (isRetry) {
    // Not updating to the use the new property due to neglibile volume of usage
    terminalMonitoringInstance.sendEvent('START', {
      transactionType: TRANSACTION_TYPE.REFUND,
    })
  }
  try {
    const collectResponse = await terminal.collectSetupIntentPaymentMethod(
      clientSecret,
      true
    )
    if (isErrorResponse(collectResponse)) {
      throw collectResponse.error
    }

    const confirmResponse = await terminal.confirmSetupIntent(
      collectResponse.setupIntent
    )
    if (isErrorResponse(confirmResponse)) {
      throw confirmResponse.error
    }

    const latestAttempt = confirmResponse.setupIntent.latest_attempt

    if (!isSetupAttempt(latestAttempt)) {
      throw new Error(
        'response from `terminal.confirmSetupIntent` was not a setup attempt'
      )
    }

    const generatedCard =
      latestAttempt.payment_method_details.card_present?.generated_card

    if (typeof generatedCard !== 'string') {
      throw new Error('generated card was not a string')
    }

    terminalMonitoringInstance.sendEvent('COMPLETE', {
      payment: {
        status: 'APPROVED',
      },
      transactionType: TRANSACTION_TYPE.REFUND,
    })
    return generatedCard
  } catch (error) {
    trackTerminalCompleteWithError(terminalMonitoringInstance, error, true)
    throw error
  }
}

const isSetupAttempt = (attempt: any): attempt is Stripe.SetupAttempt => {
  return !!(attempt as Stripe.SetupAttempt).payment_method_details
}
